import { ReactElement, useMemo } from "react"
import { CubeProvider } from "@cubejs-client/react"
import { useIdleTimer } from "react-idle-timer"

import Guest from "../Guest"
import Main from "../Main"
import { Onboarding } from "../Onboarding"
import StaticSpinner from "~/ui-rtk/components/layout/StaticSpinner"

import { useAppSession } from "./useCubeApiToken"
import useAccess from "~/ui-rtk/hooks/access"
import {
  useAllConnectorsTouched,
  useConnectionFlowIgnored,
  useOnboardingComplete,
  useOnboardingEnabled,
} from "~/ui-rtk/hooks/onboarding-flow"
import { AccountDetails, CheckInbox } from "../Onboarding/BrandInfo"
import ConfirmEmail from "../ConfirmEmail"
import SosOnboarding from "../SosOnboarding"
import StalePaywall from "../StalePaywall"
import { CompanyTier } from "~/ui-rtk/api/types/company.types"
import { useURLSearchParams } from "~/ui-rtk/hooks/url"
import { useCompanyConnectorControllerGetCompanyConnectorsQuery } from "~/ui-rtk/api/companyConnectorApi"

const MARATHONDATA_CO_NAME = "MARATHONDATA_CO"

const AuthPage = () => {
  let content = null
  const { session, cubeApi, isLoading, invalidateTokenIfNeeded } =
    useAppSession()
  const { isSuperAdmin, isAccessGranted, isStale } = useAccess()
  const { params } = useURLSearchParams()

  // pre-fetch for correct url state on brand mentions; skipped on STALE
  // sessions where every non-allowlisted request 403s anyway
  const { isLoading: isConnectorsQueryLoading } =
    useCompanyConnectorControllerGetCompanyConnectorsQuery(undefined, {
      skip: isStale,
    })

  const { user, currentCompany } = session || {}
  const isSetupStatusComplete = useOnboardingComplete()
  const isOnboardingEnabled = useOnboardingEnabled()
  const isConnectionFlowIgnored = useConnectionFlowIgnored()
  const { isEveryConnectorTouched, isLoading: isConnectorTouchedLoading } =
    useAllConnectorsTouched(!!currentCompany?.id && !isStale)

  const onActive = () => {
    invalidateTokenIfNeeded()?.catch(() => null)
  }

  const isUserActive = useMemo(
    () => user?.isConfirmed || user?.isActive,
    [user],
  )

  useIdleTimer({
    onActive,
    timeout: 30_000, // 30 seconds
    throttle: 500,
    crossTab: true,
    syncTimers: 200,
    name: MARATHONDATA_CO_NAME,
  })

  const renderMain: () => ReactElement = () =>
    isConnectorsQueryLoading ? (
      <StaticSpinner />
    ) : (
      <CubeProvider cubeApi={cubeApi}>
        <Main />
      </CubeProvider>
    )

  const renderAuthSessionContent = () => {
    if (!isUserActive && params["nonce"]) {
      return (
        <div className="w-full h-full flex items-center justify-center">
          <ConfirmEmail />
        </div>
      )
    }
    if (!isUserActive) {
      return (
        <div className="w-full h-full flex items-center justify-center">
          <CheckInbox />
        </div>
      )
    }
    if (!currentCompany?.id) {
      return (
        <div className="w-full h-full flex items-center justify-center">
          <AccountDetails />
        </div>
      )
    }
    // STALE (aged-out non-paying) companies see a full-screen paywall
    // instead of the app — one gate here covers every route, and the API
    // refuses these sessions anyway (403 COMPANY_STALE). isStale already
    // excludes super admins, who keep Main so support can inspect and
    // restore the company.
    if (isStale) {
      return <StalePaywall />
    }
    // Free Share of Search companies get their own setup flow instead of
    // the standard onboarding/connection flow (APP-444; wizard in APP-447).
    // Once the report is ready (the report-ready email has been sent,
    // APP-449) they fall through to Main so the email's deep link resolves;
    // nav gating/paywall for that state lands with APP-453. Super admins
    // keep normal Main access so support/debugging stays possible.
    if (
      currentCompany?.tier === CompanyTier.SOS_FREE &&
      !isSuperAdmin &&
      !currentCompany?.metadata?.sosReportReadyNotifiedAt
    ) {
      return <SosOnboarding />
    }
    if (!isOnboardingEnabled) {
      return renderMain()
    }
    if (isSetupStatusComplete) {
      return renderMain()
    }
    if (isEveryConnectorTouched) {
      return renderMain()
    }
    if (isConnectionFlowIgnored) {
      return renderMain()
    }
    if (isAccessGranted || isSuperAdmin) {
      return renderMain()
    }

    return <Onboarding />
  }

  if (session) {
    content = renderAuthSessionContent()
  } else if (!session && !isLoading && !isConnectorTouchedLoading) {
    content = <Guest />
  }

  return (
    <>
      <StaticSpinner />
      {content}
    </>
  )
}

export default AuthPage
